import React, { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { url } from "aldoo-ra/CMS/url"
import {
  PlusCircleIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import PlatformSelector from "aldoo-ra/PlatformSelector"
import {
  GetLicenses,
  GetPacks,
  DeleteLicenses,
  UpdateLicense,
  ValidateCode,
} from "./api"
import LicenseViewer from "./LicenseViewer/index"
import LicenseGenerator from "./LicenseGenerator"
import LicenseExportDialog from "./LicenseExportDialog"
import UserPlatformAPI from "aldoo-ra/Admin/PlatformAccess/user-api"
import { twMerge } from "tailwind-merge"

export default function Licenses() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [selectedLicenseId, setSelectedLicenseId] = useState(null)
  const [showGenerator, setShowGenerator] = useState(false)

  // Filter states
  const [statusFilter, setStatusFilter] = useState([
    { value: null, label: "All Status" },
  ])
  const [packFilter, setPackFilter] = useState([
    { value: null, label: "All Packs" },
  ])
  const [typeFilter, setTypeFilter] = useState([
    { value: null, label: "All Types" },
  ])
  const [userFilter, setUserFilter] = useState(null)
  const [platformFilter, setPlatformFilter] = useState([])
  const [packs, setPacks] = useState([])
  const [initialPackName, setInitialPackName] = useState(null)
  const [initialStatusValue, setInitialStatusValue] = useState(null)
  const [initialTypeValue, setInitialTypeValue] = useState(null)
  const [packsLoaded, setPacksLoaded] = useState(false)
  const [initialFiltersReady, setInitialFiltersReady] = useState(false)

  // Helper function to normalize pack names for comparison
  const normalizeName = (name) => {
    return name?.toLowerCase().trim().replace(/\s+/g, " ")
  }

  // Helper function to encode pack name for URL
  const encodePackName = (name) => {
    return encodeURIComponent(name)
  }

  // Helper function to decode pack name from URL
  const decodePackName = (encoded) => {
    return decodeURIComponent(encoded.replace(/\+/g, "%20"))
  }

  // Check URL for license ID, generator, or pack parameter
  useEffect(() => {
    if (typeof window !== "undefined") {
      const pathname = window.location.pathname
      const urlParams = new URLSearchParams(window.location.search)

      // Check for license ID
      const licenseMatch = pathname.match(/\/licenses\/([^\/]+)/)
      if (licenseMatch && licenseMatch[1] !== "generate") {
        setSelectedLicenseId(licenseMatch[1])
      }

      // Check for generator
      if (pathname.includes("/licenses/generate")) {
        setShowGenerator(true)
      }

      // Check for pack parameter
      const packParam = urlParams.get("pack")
      if (packParam) {
        const packName = decodePackName(packParam)
        setInitialPackName(packName)
      }

      // Check for status parameter
      const statusParam = urlParams.get("status")
      if (statusParam) {
        setInitialStatusValue(statusParam)
      }

      // Check for type parameter
      const typeParam = urlParams.get("type")
      if (typeParam) {
        setInitialTypeValue(typeParam)
      }
    }
  }, [])

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      if (typeof window !== "undefined") {
        const pathname = window.location.pathname

        // Check for license ID
        const licenseMatch = pathname.match(/\/licenses\/([^\/]+)/)
        if (licenseMatch && licenseMatch[1] !== "generate") {
          setSelectedLicenseId(licenseMatch[1])
        } else {
          setSelectedLicenseId(null)
        }

        // Check for generator
        if (pathname.includes("/licenses/generate")) {
          setShowGenerator(true)
        } else {
          setShowGenerator(false)
        }
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Load packs for filter
  useEffect(() => {
    async function loadPacks() {
      try {
        const response = await GetPacks({ itemsPerPage: -1 })
        if (response.result) {
          const packOptions = [
            { value: null, label: "All Packs" },
            ...response.data.map((pack) => ({
              value: pack._id,
              label: pack.name,
            })),
          ]
          setPacks(packOptions)
          setPacksLoaded(true)
        }
      } catch (error) {
        setPacksLoaded(true) // Mark as loaded even on error to prevent hanging
      }
    }
    loadPacks()
  }, [])

  // Apply initial filters when packs are loaded and URL parameters are parsed
  useEffect(() => {
    if (packsLoaded) {
      let filtersApplied = false

      // Apply pack filter if specified in URL
      if (initialPackName) {
        const normalizedInitialName = normalizeName(initialPackName)
        const matchingPack = packs.find(
          (pack) =>
            pack.value && normalizeName(pack.label) === normalizedInitialName
        )

        if (matchingPack) {
          setPackFilter([matchingPack])
          filtersApplied = true
        } else {
          showNotification({
            message: `Pack "${initialPackName}" not found`,
            className: "bg-admin_warning text-white",
          })
        }
        setInitialPackName(null)
      }

      // Apply status filter if specified in URL
      if (initialStatusValue) {
        const statusOptions = [
          { value: null, label: "All Status" },
          { value: "free", label: "Free" },
          { value: "active", label: "Active" },
          { value: "canceled", label: "Canceled" },
          { value: "paused", label: "Paused" },
          { value: "expired", label: "Expired" },
          { value: "revoked", label: "Revoked" },
        ]

        const matchingStatus = statusOptions.find(
          (status) => status.value === initialStatusValue
        )

        if (matchingStatus) {
          setStatusFilter([matchingStatus])
          filtersApplied = true
        } else {
          showNotification({
            message: `Status "${initialStatusValue}" not found`,
            className: "bg-admin_warning text-white",
          })
        }
        setInitialStatusValue(null)
      }

      // Apply type filter if specified in URL
      if (initialTypeValue) {
        const typeOptions = [
          { value: null, label: "All Types" },
          { value: "internal", label: "Internal Only" },
          { value: "external", label: "External Only" },
        ]

        const matchingType = typeOptions.find(
          (type) => type.value === initialTypeValue
        )

        if (matchingType) {
          setTypeFilter([matchingType])
          filtersApplied = true
        } else {
          showNotification({
            message: `Type "${initialTypeValue}" not found`,
            className: "bg-admin_warning text-white",
          })
        }
        setInitialTypeValue(null)
      }

      // Mark initial filters as ready
      setInitialFiltersReady(true)

      // Only refresh if filters were actually applied
      if (filtersApplied) {
        setRefreshKey((prev) => prev + 1)
      }
    }
  }, [packsLoaded, initialPackName, initialStatusValue, initialTypeValue, packs])

  // Update URL when pack filter changes
  const updatePackFilterWithURL = (newFilter) => {
    setPackFilter(newFilter)
    setRefreshKey((prev) => prev + 1)

    // Update URL parameter
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)

      const selectedPack = newFilter[0]
      const isSpecificPack = selectedPack?.value !== null

      if (isSpecificPack) {
        // Set pack parameter using pack name
        params.set("pack", encodePackName(selectedPack.label))
      } else {
        // Remove pack parameter when "All Packs" is selected
        params.delete("pack")
      }

      // Update URL with navigate
      const queryString = params.toString()
      const newPath =
        window.location.pathname + (queryString ? `?${queryString}` : "")

      navigate(newPath, { keepScrollPosition: true })
    }
  }

  // Update URL when status filter changes
  const updateStatusFilterWithURL = (newFilter) => {
    setStatusFilter(newFilter)
    setRefreshKey((prev) => prev + 1)

    // Update URL parameter
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)

      const selectedStatus = newFilter[0]
      const isSpecificStatus = selectedStatus?.value !== null

      if (isSpecificStatus) {
        // Set status parameter
        params.set("status", selectedStatus.value)
      } else {
        // Remove status parameter when "All Status" is selected
        params.delete("status")
      }

      // Update URL with navigate
      const queryString = params.toString()
      const newPath =
        window.location.pathname + (queryString ? `?${queryString}` : "")

      navigate(newPath, { keepScrollPosition: true })
    }
  }

  // Update URL when type filter changes
  const updateTypeFilterWithURL = (newFilter) => {
    setTypeFilter(newFilter)
    setRefreshKey((prev) => prev + 1)

    // Update URL parameter
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search)

      const selectedType = newFilter[0]
      const isSpecificType = selectedType?.value !== null

      if (isSpecificType) {
        // Set type parameter
        params.set("type", selectedType.value)
      } else {
        // Remove type parameter when "All Types" is selected
        params.delete("type")
      }

      // Update URL with navigate
      const queryString = params.toString()
      const newPath =
        window.location.pathname + (queryString ? `?${queryString}` : "")

      navigate(newPath, { keepScrollPosition: true })
    }
  }

  // Helper function to get user filter
  const getUserFilter = () => {
    if (!userFilter || !userFilter.length) return null
    const selectedUser = userFilter[0]
    return selectedUser._id
  }

  // Build match object for API
  const getRequestFields = () => {
    const match = {}

    // Status filter
    const selectedStatus = statusFilter[0]?.value
    if (selectedStatus) {
      match.status = selectedStatus
    }

    // Pack filter
    const selectedPack = packFilter[0]?.value
    if (selectedPack) {
      match.licensePack = selectedPack
    }

    // Type filter (external/internal)
    const selectedType = typeFilter[0]?.value
    if (selectedType === "external") {
      match.external = true
    } else if (selectedType === "internal") {
      match.external = { $ne: true }
    }

    // User filter
    const userFilterValue = getUserFilter()
    if (userFilterValue !== null) {
      match.owner = userFilterValue
    }

    // Platform filter
    if (platformFilter && platformFilter.length > 0) {
      const selectedPlatforms = platformFilter.map(p => p.value)
      if (!selectedPlatforms.includes("any")) {
        match.supported_platforms = { $in: selectedPlatforms }
      }
      // If "any" is selected, don't add platform filter (show all)
    }

    return { 
      match,
      populate: ["owner"]
    }
  }

  // Handle license selection
  const handleViewLicense = (licenseId) => {
    setSelectedLicenseId(licenseId)
    // Rewrite URL without navigation
    window.history.pushState(null, '', `/admin/product-licensing/licenses/${licenseId}`)
  }

  // Handle closing license viewer
  const handleCloseLicense = () => {
    setSelectedLicenseId(null)
    // Restore previous URL without navigation
    window.history.pushState(null, '', '/admin/product-licensing/licenses')
  }

  // Handle generator close
  const handleCloseGenerator = () => {
    setShowGenerator(false)
    navigate("/admin/product-licensing/licenses")
    setRefreshKey((prev) => prev + 1)
  }

  // Handle select all
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle bulk delete
  const handleDeleteSelected = () => {
    if (selectedItems.length === 0) return

    showPromptDialog({
      title: "Delete Licenses",
      message: `Are you sure you want to delete ${
        selectedItems.length
      } license${
        selectedItems.length === 1 ? "" : "s"
      }? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeleteLicenses(selectedItems)

          if (response.result) {
            showNotification({
              message: `${response.deleted} license${
                response.deleted === 1 ? "" : "s"
              } deleted successfully`,
              className: "bg-admin_primary text-admin_text_inv",
            })
            setSelectedItems([])
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || "Failed to delete licenses",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting licenses",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle status update
  const handleUpdateStatus = async (license, newStatus) => {
    try {
      const response = await UpdateLicense({
        licenseId: license._id,
        status: newStatus,
      })

      if (response.result) {
        showNotification({
          message: `License status updated to ${newStatus}`,
          className: "bg-admin_primary text-admin_text_inv",
        })
        setRefreshKey((prev) => prev + 1)
      } else {
        showNotification({
          message: response.error || "Failed to update license",
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: "An error occurred while updating license",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle export licenses
  const handleExportLicenses = () => {
    LicenseExportDialog.exportAllLicenses(getRequestFields())
  }

  // Style overrides for DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    header: "w-full flex justify-between items-end gap-2 mb-4",
    searchInput: "absolute -top-16 right-[250px] text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    getRowStyle: (index, item) => {
      if (item && selectedItems.includes(item._id)) {
                    return "bg-admin_primary/10 hover:bg-admin_primary/20"
      }
    },
  }

  // Column definitions
  const columns = [
    {
      id: "code",
      title: "License Code",
      headerClassName: "w-[200px]",
      render: (_, item) => (
        <div
          className="cursor-pointer hover:text-admin_primary"
          onClick={() => handleViewLicense(item._id)}
        >
          <div className="flex items-center space-x-2">
            <Typography 
              variant="admin_txt1" 
              className="font-mono"
            >
              {item.code}
            </Typography>
            
            {/* Show external badge if license is external */}
            {item.external && (
              <Typography 
                variant="admin_txt2"
                className="text-white font-medium bg-violet-600 rounded-full px-2 py-1"
              >
                external
              </Typography>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "status",
      title: "Status",
      headerClassName: "w-[80px]",
      render: (_, item) => {
        const statusConfig = {
          free: {
            color: "bg-admin_success text-white",
            label: "Free",
          },
          active: {
            color: "bg-admin_primary_lt text-white",
            label: "Active",
          },
          canceled: {
            color: "bg-admin_warning text-white",
            label: "Canceled",
          },
          paused: {
            color: "bg-admin_warning text-white",
            label: "Paused",
          },
          expired: {
            color: "bg-admin_error text-white",
            label: "Expired",
          },
          revoked: {
            color: "bg-admin_neutral_2 text-white",
            label: "Revoked",
          },
        }
        const config = statusConfig[item.status] || statusConfig.free

        return (
          <span
            className={twMerge(
              "px-3 py-1 rounded-full text-xs font-medium",
              config.color
            )}
          >
            {config.label}
          </span>
        )
      },
    },
    {
      id: "pack",
      title: "Pack",
      headerClassName: "w-[150px]",
      render: (_, item) => (
        <div>
          {item.licensePack?.name ? (
            <span
              className="text-admin_text_secondary hover:text-admin_primary cursor-pointer transition-colors"
              onClick={() =>
                navigate(`/admin/product-licensing/packs/${item.licensePack._id}`)
              }
            >
              {item.licensePack.name}
            </span>
          ) : (
            <span className="text-admin_text_secondary">
              No pack
            </span>
          )}
        </div>
      ),
    },
    {
      id: "owner",
      title: "Owner",
      headerClassName: "xl:w-[180px] 2xl:w-1/4",
      render: (_, item) => (
        <div className="flex items-center xl:space-x-3 py-2">
          {item.owner ? (
            <>
              {/* User Avatar */}
              <div className="hidden xl:flex w-8 h-8 items-center justify-center rounded-full bg-admin_neutral_3 flex-shrink-0">
                <Typography 
                  variant="admin_txt" 
                  className="text-white font-medium"
                >
                  {(item.owner.name || item.owner.email).charAt(0).toUpperCase()}
                </Typography>
              </div>

              {/* User Info */}
              <div className="flex flex-col justify-center min-w-0 flex-1  leading-tight">
                {item.owner.name ? (
                  <Typography 
                    variant="admin_label" 
                    className="truncate leading-none my-0"
                  >
                    {item.owner.name}
                  </Typography>
                ) : (
                  <Tooltip 
                    display="block"
                    content={
                      <div className="text-center">
                        <Typography 
                          variant="admin_txt1"
                          className="block"
                        >
                          {item.owner?.email || "No email"}
                        </Typography>
      
                        <Typography variant="admin_txt2_dim">
                          Click to copy
                        </Typography>
                      </div>
                    }
                  >
                    <Typography 
                      variant="admin_label" 
                      className="truncate leading-none"
                    >
                      {item.owner.email}
                    </Typography>
                  </Tooltip>
                )}

                {item.owner.name && (
                  <Tooltip 
                    display="block"
                    content={
                      <div className="text-center">
                        <Typography 
                          variant="admin_txt1"
                          className="block"
                        >
                          {item.owner?.email || "No email"}
                        </Typography>
      
                        <Typography variant="admin_txt2_dim">
                          Click to copy
                        </Typography>
                      </div>
                    }
                  >
                    <button 
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(item.owner?.email)
                          showNotification({
                            message: "Copied to clipboard",
                            className: "bg-admin_primary text-white"
                          })
                        } catch (error) {
                          console.error("Failed to copy:", error)
                        }
                      }}
                      className="w-full truncate whitespace-nowrap text-admin_text_secondary hover:text-admin_primary transition-colors text-left"
                    >
                      {item.owner?.email || "No email"}
                    </button>
                  </Tooltip>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Unassigned Avatar */}
              <div className="hidden xl:flex w-8 h-8 items-center justify-center rounded-full bg-admin_neutral_1 flex-shrink-0">
                <Typography 
                  variant="admin_txt_dim" 
                  className="font-medium"
                >
                  ?
                </Typography>
              </div>

              {/* Unassigned Text */}
              <div className="flex items-center">
                <Typography variant="admin_txt1_dim">
                  Unassigned
                </Typography>
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      id: "seats",
      title: "Seats",
      headerClassName: "w-[100px] text-center",
      render: (_, item) => (
        <div className="w-full flex items-center justify-center">
          <Typography 
            variant="admin_body" 
            className="text-center my-0"
          >
            {item.usedSeats || 0} / {item.seats}
          </Typography>
        </div>
      ),
    },
    {
      id: "product",
      title: "Product",
      headerClassName: "w-[130px] hidden xl:table-cell",
      itemClassName: "hidden xl:table-cell",
      render: (_, item) => (
        <Typography variant="admin_txt1_dim">
          {item.productId || "Not set"}
        </Typography>
      ),
    },
    {
      id: "platforms",
      title: "Platforms",
      headerClassName: "w-[120px] text-center",
      render: (_, item) => {
        const platforms = item.supported_platforms || []
        
        // If no platforms specified or includes "any", show all platforms icon
        if (platforms.length === 0 || platforms.includes("any")) {
          return (
            <div className="flex items-center justify-center">
              <Tooltip content="All Platforms">
                <div className="flex items-center justify-center w-6 h-6">
                  {renderPlatformIcon("any", "w-full h-full text-admin_text_secondary")}
                </div>
              </Tooltip>
            </div>
          )
        }
        
        // Show specific platform icons
        return (
          <div className="w-full flex items-center justify-center gap-1 flex-wrap">
            {platforms.map((platform, index) => (
              <Tooltip key={platform || index} content={getPlatformDisplayName(platform)}>
                <div className="flex items-center justify-center w-6 h-6">
                  {renderPlatformIcon(platform, "w-full h-full text-admin_text_secondary")}
                </div>
              </Tooltip>
            ))}
          </div>
        )
      },
    },
  ]

  // Header controls
  const headerControls = (
    <div className="w-full flex flex-row items-end gap-3">
      {/* Status filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Status
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All Status" },
            { value: "free", label: "Free" },
            { value: "active", label: "Active" },
            { value: "canceled", label: "Canceled" },
            { value: "paused", label: "Paused" },
            { value: "expired", label: "Expired" },
            { value: "revoked", label: "Revoked" },
          ]}
          value={statusFilter}
          onChange={(values) => {
            const newFilter = values?.length
              ? [values[0]]
              : [{ value: null, label: "All Status" }]

            updateStatusFilterWithURL(newFilter)
          }}
          className="w-[120px]"
          showCheckbox={false}
        />
      </div>

      {/* Pack filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          License Pack
        </Typography>

        <DropDownSelect
          variant="admin"
          options={packs}
          value={packFilter}
          onChange={(values) => {

            const newFilter = values?.length
              ? [values[0]]
              : [{ value: null, label: "All Packs" }]

            updatePackFilterWithURL(newFilter)
          }}
          className="w-[160px]"
          showCheckbox={false}
          searchEnabled
        />
      </div>

      {/* User filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Owner
        </Typography>

        <DropDownSelect
          variant="admin"
          searchEnabled
          useExternalSearch
          enableResetButton
          showCheckbox={false}
          apiMethod={async (params) => {
            if (!params.searchTerm || params.searchTerm.length < 2) {
              return { data: [] }
            }

            const response = await UserPlatformAPI.SearchUsers(params.searchTerm)
            return response
          }}
          onDataLoaded={(rawData) => {
            return rawData.map(user => ({
              _id: user._id,
              name: user.name || 'Unknown User',
              email: user.email || 'No email',
              value: user._id,
              label: `${user.name || 'Unknown User'} (${user.email || 'No email'})`,
              ...user
            }))
          }}
          searchField="name email"
          valueField="_id"
          nameField="label"
          className="w-[200px] xl:w-[320px]"
          placeholder="Filter by owner"
          noDataMessage="Filter by owner"
          value={userFilter}
          onChange={(value) => {
            setUserFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
          onValueItemRender={(user) => {
            if (!user) return "Filter by owner"
            return (
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                  {user.avatar ? (
                    <img src={url(user.avatar)} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <span className="text-white font-medium text-xs">
                      {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-admin_text truncate">
                    {user.name || 'Unknown User'}
                  </div>
                  <div className="text-xs text-admin_text_secondary truncate">
                    {user.email || 'No email'}
                  </div>
                </div>
              </div>
            )
          }}
          onItemRender={(user) => (
            <div className="flex items-center gap-3 p-2">
              <div className="w-8 h-8 rounded-full bg-admin_primary flex items-center justify-center flex-shrink-0">
                {user.avatar ? (
                  <img src={url(user.avatar)} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-medium text-sm">
                    {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {user.name || 'Unknown User'}
                </div>
                <div className="text-xs text-gray-500">
                  {user.email || 'No email'}
                </div>
              </div>
            </div>
          )}
        />
      </div>

      {/* Platform filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Platform
        </Typography>

        <PlatformSelector
          platforms={[
            { value: "any", label: "All Platforms" },
            { value: "ios", label: "iOS" },
            { value: "android", label: "Android" },
            { value: "windows", label: "Windows" },
            { value: "linux", label: "Linux" },
            { value: "macos", label: "macOS" },
            { value: "web", label: "Web" },
          ]}
          value={platformFilter}
          onChange={(values) => {
            setPlatformFilter(values)
            setRefreshKey((prev) => prev + 1)
          }}
          fixedWidth="170px"
          placeholder="Filter by platform"
        />
      </div>
    </div>
  )

  // Show license viewer if selected
  if (selectedLicenseId) {
    return <LicenseViewer licenseId={selectedLicenseId} onClose={handleCloseLicense} />
  }

  // Show license generator if open
  if (showGenerator) {
    return <LicenseGenerator onClose={handleCloseGenerator} />
  }

  // Show main content
  if (!initialFiltersReady) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-admin_primary mx-auto mb-4"></div>
          <Typography variant="admin_body" className="text-admin_text_secondary">
            Loading filters...
          </Typography>
        </div>
      </div>
    )
  }

  {/* Table footer content - action buttons */}
  return (
    <div className="p-6">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Licenses
        </Typography>

        <div className="flex items-end justify-end gap-3">
          <Button
            variant="admin_secondary"
            className="whitespace-nowrap flex items-center"
            onClick={handleExportLicenses}
          >
            <ArrowDownTrayIcon className="hidden xl:block w-5 h-5 mr-1" />
            Export
          </Button>

          <Button
            variant="admin_primary"
            className="whitespace-nowrap flex items-center gap-1"
            onClick={() => {
              setShowGenerator(true)
              navigate("/admin/product-licensing/licenses/generate")
            }}
          >
            <PlusCircleIcon className="w-5 h-5 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <DataTable
        variant="admin_browser"
        apiMethod={GetLicenses}
        requestFields={getRequestFields()}
        headerControls={headerControls}
        enableSelection={true}
        selectedItems={selectedItems}
        onSelectionChange={(ids) => setSelectedItems(ids)}
        bulkActions={[
          {
            label: "Delete",
            variant: "admin_error",
            icon: <TrashIcon className="w-5 h-5" />,
            onClick: handleDeleteSelected,
          }
        ]}
        itemIdField="_id"
        searchField="code owner.email owner.name productId"
        sort={{ created: -1 }}
        localUserPreferencesKey="admin.licensing.licenses"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="65vh"
        itemHeight="h-[60px]"
        styleOverrides={styleOverrides}
        noData={
          <div className="text-center py-8">
            <DocumentDuplicateIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />

            <Typography
              variant="admin_body_dim"
            >
              No licenses found
            </Typography>

            <Button
              variant="admin_primary"
              className="mt-4"
              onClick={() => {
                setShowGenerator(true)
                navigate("/admin/product-licensing/licenses/generate")
              }}
            >
              Generate First Licenses
            </Button>
          </div>
        }
      />
    </div>
  )
}
